import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchAvailableModels, getModelCategories, ModelInfo } from '../services/modelService';
import { useLanguage } from '../hooks/useLanguage';

interface OpenRouterConfigProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: OpenRouterConfig) => void;
  currentConfig?: OpenRouterConfig;
}

export interface OpenRouterConfig {
  apiKey: string;
  model: string;
  temperature: number;
  maxTokens: number;
}

export const OpenRouterConfig: React.FC<OpenRouterConfigProps> = ({
  isOpen,
  onClose,
  onSave,
  currentConfig
}) => {
  const { language, isPortuguese } = useLanguage();
  const { t } = useTranslation();
  const [config, setConfig] = useState<OpenRouterConfig>({
    apiKey: '',
    model: 'anthropic/claude-3.5-sonnet',
    temperature: 0.7,
    maxTokens: 2000,
    ...currentConfig
  });

  const [showApiKey, setShowApiKey] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [availableModels, setAvailableModels] = useState<ModelInfo[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'popular' | 'premium' | 'economic' | 'specialized'>('all');

  useEffect(() => {
    if (currentConfig) {
      setConfig(currentConfig);
    }
  }, [currentConfig]);

  // Load available models when component opens
  useEffect(() => {
    if (isOpen && availableModels.length === 0) {
      setIsLoadingModels(true);
      fetchAvailableModels()
        .then(models => {
          setAvailableModels(models);
        })
        .catch(error => {
          console.error('Failed to load models:', error);
        })
        .finally(() => {
          setIsLoadingModels(false);
        });
    }
  }, [isOpen, availableModels.length]);

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  const testConnection = async () => {
    if (!config.apiKey.trim()) {
      alert(t('openRouterConfig.pleaseInsertApiKey'));
      return;
    }

    setTestStatus('testing');
    try {
      const response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${config.apiKey}`,
          'Content-Type': 'application/json',
          'HTTP-Referer': window.location.origin,
          'X-Title': 'Prompt Editor - Aldeia Viva'
        },
        body: JSON.stringify({
          model: config.model,
          messages: [{ role: 'user', content: 'Hello, this is a test.' }],
          max_tokens: 10,
          temperature: 0.1
        })
      });

      if (response.ok) {
        setTestStatus('success');
        setTimeout(() => setTestStatus('idle'), 3000);
      } else {
        setTestStatus('error');
        setTimeout(() => setTestStatus('idle'), 3000);
      }
    } catch (error) {
      setTestStatus('error');
      setTimeout(() => setTestStatus('idle'), 3000);
    }
  };

  // Filter models by category
  const filteredModels = selectedCategory === 'all' 
    ? availableModels 
    : availableModels.filter(model => model.category === selectedCategory);

  // Get category labels
  const categories = getModelCategories(language);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-ghost-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-ghost-800">{t('openRouterConfig.title')}</h2>
              <p className="text-sm text-ghost-600 mt-1">{t('openRouterConfig.subtitle')}</p>
            </div>
            <button
              onClick={onClose}
              className="text-ghost-400 hover:text-ghost-600 transition-colors p-1"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-ghost-700 mb-2">
              {t('openRouterConfig.apiKeyLabel')}
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={config.apiKey}
                onChange={(e) => setConfig({ ...config, apiKey: e.target.value })}
                placeholder="sk-or-..."
                className="w-full px-3 py-2 border border-ghost-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm pr-20"
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="text-ghost-400 hover:text-ghost-600 p-1"
                >
                  {showApiKey ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
                <button
                  type="button"
                  onClick={testConnection}
                  disabled={testStatus === 'testing'}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    testStatus === 'testing' 
                      ? 'bg-yellow-100 text-yellow-700'
                      : testStatus === 'success'
                      ? 'bg-green-100 text-green-700'
                      : testStatus === 'error'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-ghost-100 text-ghost-700 hover:bg-ghost-200'
                  }`}
                >
                  {testStatus === 'testing' && t('openRouterConfig.testing')}
                  {testStatus === 'success' && t('openRouterConfig.success')}
                  {testStatus === 'error' && t('openRouterConfig.error')}
                  {testStatus === 'idle' && t('openRouterConfig.test')}
                </button>
              </div>
            </div>
            <p className="text-xs text-ghost-500 mt-1">
              {t('openRouterConfig.getApiKeyText')} <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">openrouter.ai/keys</a>
            </p>
          </div>

          {/* Model Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-ghost-700">
                {t('openRouterConfig.modelLabel')}
              </label>
              {isLoadingModels && (
                <span className="text-xs text-ghost-500">
                  {t('openRouterConfig.loading')}
                </span>
              )}
            </div>

            {/* Category Filter */}
            <div className="mb-3">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    selectedCategory === 'all'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-ghost-100 text-ghost-600 hover:bg-ghost-200'
                  }`}
                >
                  {t('openRouterConfig.categories.all')}
                </button>
                <button
                  onClick={() => setSelectedCategory('popular')}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    selectedCategory === 'popular'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-ghost-100 text-ghost-600 hover:bg-ghost-200'
                  }`}
                >
                  🔥 {categories.popular}
                </button>
                <button
                  onClick={() => setSelectedCategory('premium')}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    selectedCategory === 'premium'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-ghost-100 text-ghost-600 hover:bg-ghost-200'
                  }`}
                >
                  💎 {categories.premium}
                </button>
                <button
                  onClick={() => setSelectedCategory('economic')}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    selectedCategory === 'economic'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-ghost-100 text-ghost-600 hover:bg-ghost-200'
                  }`}
                >
                  💰 {categories.economic}
                </button>
              </div>
            </div>

            {/* Model Dropdown */}
            <select
              value={config.model}
              onChange={(e) => setConfig({ ...config, model: e.target.value })}
              className="w-full px-3 py-2 border border-ghost-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={isLoadingModels}
            >
              {filteredModels.map((model) => (
                <option key={model.id} value={model.id}>
                  {model.isRecommended ? '⭐ ' : ''}{model.name} ({model.provider})
                  {model.pricing && ` - $${model.pricing.prompt.toFixed(4)}/1K tokens`}
                </option>
              ))}
            </select>

            {/* Model Description */}
            {(() => {
              const currentModel = availableModels.find(m => m.id === config.model);
              if (currentModel?.description) {
                return (
                  <p className="text-xs text-ghost-500 mt-1">
                    {currentModel.description}
                  </p>
                );
              }
              return null;
            })()}
          </div>

          {/* Temperature */}
          <div>
            <label className="block text-sm font-medium text-ghost-700 mb-2">
              {t('openRouterConfig.temperature')}: {config.temperature}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={config.temperature}
              onChange={(e) => setConfig({ ...config, temperature: parseFloat(e.target.value) })}
              className="w-full h-2 bg-ghost-200 rounded-lg appearance-none cursor-pointer slider"
            />
            <div className="flex justify-between text-xs text-ghost-500 mt-1">
              <span>{t('openRouterConfig.moreDeterministic')}</span>
              <span>{t('openRouterConfig.moreCreative')}</span>
            </div>
          </div>

          {/* Max Tokens */}
          <div>
            <label className="block text-sm font-medium text-ghost-700 mb-2">
              {t('openRouterConfig.maxTokensLabel')}
            </label>
            <input
              type="number"
              min="100"
              max="4000"
              value={config.maxTokens}
              onChange={(e) => setConfig({ ...config, maxTokens: parseInt(e.target.value) || 2000 })}
              className="w-full px-3 py-2 border border-ghost-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
            <p className="text-xs text-ghost-500 mt-1">
              {t('openRouterConfig.maxTokensHint')}
            </p>
          </div>
        </div>

        <div className="p-6 border-t border-ghost-200 bg-ghost-50 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-ghost-700 hover:text-ghost-900 transition-colors text-sm font-medium"
          >
            {t('openRouterConfig.cancel')}
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            {t('openRouterConfig.save')}
          </button>
        </div>
      </div>
    </div>
  );
};